import type { TopicId, TopicOverview } from "@repo/app-types/entities/topic";
import { SimpleTooltip } from "@repo/ui/tooltip/simpleTooltip";
import { TooltipTrigger } from "@repo/ui/tooltip/tooltipTrigger";
import {
  IconArrowDown,
  IconArrowUp,
  IconChevronRight,
  IconInfoCircle,
} from "@tabler/icons-react";
import { useCallback, useMemo, useState, type FC, type Key } from "react";
import {
  Button as AriaButton,
  Tree,
  TreeItem,
  TreeItemContent,
} from "react-aria-components";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { twMerge } from "tailwind-merge";
import { appPaths } from "../../../app/appPaths.js";
import { formatCompactNumber } from "../../signals/signalStatsFormatters.js";
import { SignedImpactCell } from "../signedImpactCell.js";
import type { TopicCustomerExperienceEntry } from "../topicStatsFormatters.js";

interface TopicsTableProps {
  topics: readonly TopicOverview[];
  callCount: number;
  winImpactByTopic: ReadonlyMap<TopicId, number | null>;
  customerExperienceByTopic: ReadonlyMap<
    TopicId,
    TopicCustomerExperienceEntry | null
  >;
  selectedTopicId: TopicId | null;
}

type SortKey = "volume" | "winImpact" | "customerExperienceScoreImpact";
type SortDescriptor = { key: SortKey; direction: "asc" | "desc" };

const DEFAULT_SORT: SortDescriptor = { key: "winImpact", direction: "desc" };

/**
 * Tailwind grid templates shared by the header row and every body row
 * so columns align across the whole card. Topic column flexes; metric
 * columns have fixed widths so the two grids (header and Tree) compute
 * identical layouts — `auto` would let them diverge based on per-grid
 * content and misalign the header over the body.
 */
const GRID_COLS = "grid-cols-[minmax(0,1fr)_176px_200px_264px]";

const DESCRIPTION_MAX_CHARS = 175;

/** Cap long descriptions near `DESCRIPTION_MAX_CHARS` and append an
 * ellipsis so the secondary line doesn't push row height. Extends to
 * the next whitespace so we never cut mid-word. */
function truncateDescription(text: string): string {
  if (text.length <= DESCRIPTION_MAX_CHARS) return text;
  const nextBoundary = text.slice(DESCRIPTION_MAX_CHARS).search(/\s/);
  const end =
    nextBoundary === -1 ? text.length : DESCRIPTION_MAX_CHARS + nextBoundary;
  if (end >= text.length) return text;
  return `${text.slice(0, end)}…`;
}

/**
 * Pull a topic's customer-experience score, or `null` for parents (the
 * cell stays blank for grouping nodes — only leaves get a pill).
 */
function weightedScoreIfLeaf(
  topicId: TopicId,
  entries: ReadonlyMap<TopicId, TopicCustomerExperienceEntry | null>,
): number | null {
  const entry = entries.get(topicId);
  return entry?.kind === "leaf" ? entry.weightedScore : null;
}

interface SortLookups {
  winImpactByTopic: ReadonlyMap<TopicId, number | null>;
  customerExperienceByTopic: ReadonlyMap<
    TopicId,
    TopicCustomerExperienceEntry | null
  >;
}

function selfSortValue(
  topic: TopicOverview,
  descriptor: SortDescriptor,
  lookups: SortLookups,
): number | null {
  if (descriptor.key === "volume") {
    return topic.matchCount;
  }
  if (descriptor.key === "winImpact") {
    return lookups.winImpactByTopic.get(topic.topicId) ?? null;
  }
  // Experience-score column: leaves sort by their own weighted score;
  // parents sort by their most extreme child in the direction of the
  // sort (most positive when descending, most negative when ascending)
  // so a category sits next to whichever subtopic put it there.
  // Topics with no scored calls sink to the bottom.
  const entry = lookups.customerExperienceByTopic.get(topic.topicId);
  if (entry == null) {
    return null;
  }
  if (entry.kind === "leaf") {
    return entry.weightedScore;
  }
  return descriptor.direction === "desc"
    ? entry.bestWeightedScore
    : entry.worstWeightedScore;
}

function compareBy(
  a: TopicOverview,
  b: TopicOverview,
  descriptor: SortDescriptor,
  lookups: SortLookups,
): number {
  const aValue = selfSortValue(a, descriptor, lookups);
  const bValue = selfSortValue(b, descriptor, lookups);
  // Make sure null values are always at the bottom.
  if (aValue === null && bValue === null) {
    return 0;
  }
  if (aValue === null) {
    return 1;
  }
  if (bValue === null) {
    return -1;
  }

  const delta = aValue - bValue;
  return descriptor.direction === "asc" ? delta : -delta;
}

interface OrderedParent {
  readonly topic: TopicOverview;
  readonly children: readonly TopicOverview[];
}

/**
 * Order parents among themselves and children within each parent by
 * the active sort descriptor. Both levels honor the same key/direction.
 * Parents rank by their effective metric (max of sub-topics, baked
 * into the lookup maps).
 */
function orderTopicRows(
  topics: readonly TopicOverview[],
  descriptor: SortDescriptor,
  lookups: SortLookups,
): readonly OrderedParent[] {
  const topicsById = new Map(topics.map((t) => [t.topicId, t]));
  const childrenByParent = new Map<TopicId, TopicOverview[]>();
  for (const topic of topics) {
    if (!topic.parentId) continue;
    const bucket = childrenByParent.get(topic.parentId) ?? [];
    bucket.push(topic);
    childrenByParent.set(topic.parentId, bucket);
  }

  const parents = topics
    .filter((t) => !t.parentId && topicsById.has(t.topicId))
    .slice()
    .sort((a, b) => compareBy(a, b, descriptor, lookups));

  return parents.map((parent) => {
    const children = (childrenByParent.get(parent.topicId) ?? [])
      .slice()
      .sort((a, b) => compareBy(a, b, descriptor, lookups));
    return { topic: parent, children };
  });
}

export const TopicsTable: FC<TopicsTableProps> = ({
  topics,
  callCount,
  winImpactByTopic,
  customerExperienceByTopic,
}) => {
  const { playbookId: playbookIdParam } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [sortDescriptor, setSortDescriptor] =
    useState<SortDescriptor>(DEFAULT_SORT);

  const sortLookups = useMemo<SortLookups>(
    () => ({
      winImpactByTopic,
      customerExperienceByTopic,
    }),
    [winImpactByTopic, customerExperienceByTopic],
  );
  const orderedParents = useMemo(
    () => orderTopicRows(topics, sortDescriptor, sortLookups),
    [topics, sortDescriptor, sortLookups],
  );

  const expandedKeys = useMemo(() => {
    const raw = searchParams.get("expand");
    // No `expand` param at all → start with every parent open, so the
    // user sees actionable sub-topics without an extra click. An
    // explicit empty value (`?expand=`) means "user collapsed all" —
    // honor that. Any populated value is the user's explicit selection.
    if (raw === null) {
      return new Set<string>(orderedParents.map((p) => p.topic.topicId));
    }
    return new Set(raw.split(",").filter((s) => s.length > 0));
  }, [searchParams, orderedParents]);

  const onExpandedChange = useCallback(
    (keys: Set<Key>) => {
      const joined = [...keys]
        .filter((k): k is string => typeof k === "string")
        .join(",");
      const nextParams = new URLSearchParams(searchParams);
      // Always write the param so an empty string sticks as "user
      // explicitly collapsed all" instead of falling back to the
      // default-expanded state on the next render.
      nextParams.set("expand", joined);
      setSearchParams(nextParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const navigateToTopic = useCallback(
    (topicId: TopicId) => {
      const qs = searchParams.toString();
      const suffix = qs ? `?${qs}` : "";
      navigate(
        `${appPaths.playbookTopic.build({ playbookId: playbookIdParam!, topicId })}${suffix}`,
      );
    },
    [navigate, playbookIdParam, searchParams],
  );

  const toggleSort = useCallback((key: SortKey) => {
    setSortDescriptor((prev) => {
      if (prev.key !== key) return { key, direction: "desc" };
      return {
        key,
        direction: prev.direction === "desc" ? "asc" : "desc",
      };
    });
  }, []);

  // Rendered full bleed: the surrounding `TableCard` provides the single
  // border, rounding, and background.
  return (
    <div className="flex flex-col">
      <div
        className={twMerge(
          "grid items-center border-b border-border-light bg-gray-50 py-3 text-xs font-semibold text-foreground",
          GRID_COLS,
        )}
      >
        <div className="px-3 sm:px-6">TOPIC</div>
        <HeaderSortButton
          label="VOLUME"
          active={sortDescriptor.key === "volume"}
          direction={sortDescriptor.direction}
          onClick={() => toggleSort("volume")}
          tooltip="Calls where this topic came up. The bar shows its share of all calls shown."
        />
        <HeaderSortButton
          label="WIN RATE IMPACT"
          active={sortDescriptor.key === "winImpact"}
          direction={sortDescriptor.direction}
          onClick={() => toggleSort("winImpact")}
          tooltip="How much this topic is pulling overall win rate up or down. Common topics that differ slightly from average can have more impact than rare topics that differ a lot. “Positive” and “Very positive” mean this topic is pulling win rate up."
        />
        <HeaderSortButton
          label="CX IMPACT"
          active={sortDescriptor.key === "customerExperienceScoreImpact"}
          direction={sortDescriptor.direction}
          onClick={() => toggleSort("customerExperienceScoreImpact")}
          tooltip="How much this topic is pulling overall CX score up or down. Common topics that differ slightly from average can have more impact than rare topics that differ a lot. “Positive” and “Very positive” mean this topic is pulling CX up."
        />
      </div>

      <Tree
        aria-label="Topics"
        className={twMerge("grid grid-flow-row text-sm", GRID_COLS)}
        expandedKeys={expandedKeys}
        onExpandedChange={onExpandedChange}
      >
        {orderedParents.map((parent) => {
          const hasChildren = parent.children.length > 0;
          return (
            <TreeItem
              key={parent.topic.topicId}
              id={parent.topic.topicId}
              textValue={parent.topic.name}
              className={twMerge(
                "group col-span-full grid grid-cols-subgrid border-b border-gray-100 bg-gray-50 last:border-b-0",
                !hasChildren && "cursor-pointer hover:bg-blue-50",
              )}
              onAction={
                hasChildren
                  ? undefined
                  : () => navigateToTopic(parent.topic.topicId)
              }
            >
              <TreeItemContent>
                <TopicRowCells
                  topic={parent.topic}
                  callCount={callCount}
                  hasChildren={hasChildren}
                  winImpactValue={
                    winImpactByTopic.get(parent.topic.topicId) ?? null
                  }
                  customerExperienceScore={weightedScoreIfLeaf(
                    parent.topic.topicId,
                    customerExperienceByTopic,
                  )}
                />
              </TreeItemContent>
              {parent.children.map((child) => (
                <TreeItem
                  key={child.topicId}
                  id={child.topicId}
                  textValue={child.name}
                  className="group col-span-full grid cursor-pointer grid-cols-subgrid border-b border-gray-100 last:border-b-0 hover:bg-blue-50 data-[hovered]:bg-blue-50"
                  onAction={() => navigateToTopic(child.topicId)}
                >
                  <TreeItemContent>
                    <TopicRowCells
                      topic={child}
                      callCount={callCount}
                      hasChildren={false}
                      winImpactValue={
                        winImpactByTopic.get(child.topicId) ?? null
                      }
                      customerExperienceScore={weightedScoreIfLeaf(
                        child.topicId,
                        customerExperienceByTopic,
                      )}
                    />
                  </TreeItemContent>
                </TreeItem>
              ))}
            </TreeItem>
          );
        })}
      </Tree>
    </div>
  );
};

const HeaderSortButton: FC<{
  label: string;
  active: boolean;
  direction: SortDescriptor["direction"];
  onClick: () => void;
  tooltip: string;
}> = ({ label, active, direction, onClick, tooltip }) => (
  // The tooltip hangs off the sort button itself; the info icon is only an
  // affordance saying there is something to hover, never its own target.
  <TooltipTrigger>
    <AriaButton
      onPress={onClick}
      className="group inline-flex items-center justify-self-start px-3 text-left whitespace-nowrap hover:text-gray-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:px-6"
    >
      <span className="min-w-0">{label}</span>
      <IconInfoCircle
        aria-hidden="true"
        className="ml-1 size-4 shrink-0 text-muted-foreground"
      />
      {active ? (
        direction === "desc" ? (
          <IconArrowDown className="ml-2 size-3 shrink-0" />
        ) : (
          <IconArrowUp className="ml-2 size-3 shrink-0" />
        )
      ) : null}
    </AriaButton>
    <SimpleTooltip>{tooltip}</SimpleTooltip>
  </TooltipTrigger>
);

/**
 * One row's cells (Topic / Volume / Win Impact / Experience Score).
 * Rendered as siblings inside a `grid-cols-subgrid` TreeItem so they
 * snap to the card-level grid template.
 */
const TopicRowCells: FC<{
  topic: TopicOverview;
  callCount: number;
  hasChildren: boolean;
  winImpactValue: number | null;
  customerExperienceScore: number | null;
}> = ({
  topic,
  callCount,
  hasChildren,
  winImpactValue,
  customerExperienceScore,
}) => {
  const share = callCount > 0 ? topic.matchCount / callCount : 0;

  return (
    <>
      <div
        className={twMerge(
          "flex min-w-0 gap-2 px-3 py-2 sm:px-6",
          // Parent rows are intentionally a single line tall (no
          // description, tighter vertical padding) so the theme acts as
          // a slim section header. Sub-topics keep the multi-line layout
          // so the description has room to breathe.
          hasChildren ? "items-center" : "items-start sm:py-4",
        )}
      >
        {hasChildren ? (
          <AriaButton
            slot="chevron"
            className="shrink-0 text-muted-foreground hover:text-gray-700"
            aria-label="Toggle"
          >
            <IconChevronRight className="size-4 transition-transform group-aria-expanded:rotate-90" />
          </AriaButton>
        ) : (
          <span
            className={twMerge("size-4 shrink-0", !hasChildren && "mt-1")}
            aria-hidden="true"
          />
        )}
        <div className="min-w-0 flex-1">
          <div
            className={twMerge(
              "truncate text-sm text-foreground",
              hasChildren ? "font-semibold" : "font-medium",
            )}
          >
            {topic.name}
          </div>
          {!hasChildren && topic.description ? (
            <div className="line-clamp-2 text-xs text-muted-foreground">
              {truncateDescription(topic.description)}
            </div>
          ) : null}
        </div>
      </div>

      <div
        className={twMerge(
          "flex items-center justify-start gap-2 px-3 py-2 tabular-nums sm:px-6",
          !hasChildren && "sm:py-4",
        )}
      >
        <div className="h-1.5 w-24 shrink-0 overflow-hidden rounded-full bg-gray-100">
          <div
            className="h-full bg-blue-600"
            style={{ width: `${Math.min(Math.max(share, 0), 1) * 100}%` }}
          />
        </div>
        <span className="shrink-0 text-sm text-foreground">
          {formatCompactNumber(topic.matchCount)}
        </span>
      </div>

      {/* Impact cells are intentionally blank for parent topics —
          rolled-up impact across a theme isn't actionable, so we leave
          those cells empty and only render values for individual
          sub-topics. */}
      <div
        className={twMerge(
          "flex items-center justify-start px-3 py-2 sm:px-6",
          !hasChildren && "sm:py-4",
        )}
      >
        {hasChildren ? null : (
          <SignedImpactCell value={winImpactValue} unit="pp" decimals={1} />
        )}
      </div>

      <div
        className={twMerge(
          "flex items-center justify-start px-3 py-2 sm:px-6",
          !hasChildren && "sm:py-4",
        )}
      >
        {hasChildren ? null : (
          <SignedImpactCell value={customerExperienceScore} unit="pts" />
        )}
      </div>
    </>
  );
};
